import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { MediaLibrary } from "@/components/MediaLibrary";
import { BulkUpload } from "@/components/BulkUpload";
import { PromoManager } from "@/components/PromoManager";
import { toast } from "@/components/ui/sonner";

const initialEvent = { title: "", date: "", location: "", category: "both", description: "", album_price: 85, featured: false };
const initialPhoto = { event_id: "", title: "", price: 4.99, vehicle: "", rider: "", file: null, exif: "{}" };

export default function Admin() {
  const { user, loading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [eventForm, setEventForm] = useState(initialEvent);
  const [photoForm, setPhotoForm] = useState(initialPhoto);
  const [autoLabel, setAutoLabel] = useState(true);
  const [saving, setSaving] = useState(false);
  const [spotlightId, setSpotlightId] = useState("");
  const [mediaVersion, setMediaVersion] = useState(0);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [authCover, setAuthCover] = useState(null);
  const [authCoverFile, setAuthCoverFile] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  async function load() {
    const [summaryResponse, eventsResponse, ordersResponse, subscribersResponse, usersResponse, authCoverResponse, messagesResponse] = await Promise.all([api.get("/admin/summary"), api.get("/admin/events"), api.get("/admin/orders"), api.get("/admin/subscribers"), api.get("/admin/users"), api.get("/settings/auth-cover"), api.get("/admin/messages")]);
    setSummary(summaryResponse.data);
    setEvents(eventsResponse.data);
    setOrders(ordersResponse.data);
    setSubscribers(subscribersResponse.data);
    setUsers(usersResponse.data);
    setAuthCover(authCoverResponse.data);
    setMessages(messagesResponse.data);
    if (!photoForm.event_id && eventsResponse.data[0]) setPhotoForm((current) => ({ ...current, event_id: eventsResponse.data[0].id }));
  }

  useEffect(() => {
    if (user?.role === "admin") load().catch((error) => toast.error(apiError(error)));
  }, [user]);

  if (loading) return <div data-testid="admin-loading-state" className="min-h-screen bg-onyx pt-40 text-center font-mono text-xs uppercase tracking-[0.3em] text-gold">Loading admin</div>;
  if (!user) return <Navigate to="/account" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  async function notifyEvent(eventItem, count) {
    if (!count) return toast.info("No subscribers to notify yet");
    if (!window.confirm(`Email ${count} subscriber(s) that ${eventItem.title} photos are live?`)) return;
    try {
      const response = await api.post(`/admin/events/${eventItem.id}/notify`);
      toast.success(`Notified ${response.data.notified} attendee(s)`);
      await load();
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  async function toggleFeatured(eventItem) {
    try {
      await api.put(`/admin/events/${eventItem.id}`, { featured: !eventItem.featured });
      toast.success(eventItem.featured ? `${eventItem.title} removed from featured` : `${eventItem.title} is now featured on the home page`);
      await load();
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  async function createEvent(event) {
    event.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/events", { ...eventForm, album_price: Number(eventForm.album_price) });
      toast.success("Event created");
      setEventForm(initialEvent);
      await load();
    } catch (error) { toast.error(apiError(error)); } finally { setSaving(false); }
  }

  async function uploadPhoto(event) {
    event.preventDefault();
    if (!photoForm.file) return toast.error("Choose an image file");
    setSaving(true);
    try {
      const data = new FormData();
      data.append("file", photoForm.file);
      data.append("title", photoForm.title);
      data.append("price", String(photoForm.price));
      data.append("vehicle", photoForm.vehicle);
      data.append("rider", photoForm.rider);
      data.append("exif", photoForm.exif || "{}");
      data.append("auto_label", autoLabel ? "true" : "false");
      await api.post(`/admin/events/${photoForm.event_id}/photos`, data);
      toast.success("Photo uploaded and watermarked");
      setPhotoForm({ ...initialPhoto, event_id: photoForm.event_id });
      await load();
    } catch (error) { toast.error(apiError(error)); } finally { setSaving(false); }
  }

  async function updateEventDetails(eventItem, updates) {
    try {
      await api.put(`/admin/events/${eventItem.id}`, updates);
      toast.success("Event updated");
      await load();
      return true;
    } catch (error) {
      toast.error(apiError(error));
      return false;
    }
  }

  async function featureFromSpotlight() {
    const eventItem = events.find((item) => item.id === spotlightId);
    if (!eventItem) return;
    await toggleFeatured(eventItem);
    setSpotlightId("");
  }

  async function makeHero(eventItem) {
    try {
      await api.put(`/admin/events/${eventItem.id}/hero`);
      toast.success(`${eventItem.title} is now the home page hero`);
      await load();
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  async function updateAlbumPrice(eventItem, price) {
    try {
      await api.put(`/admin/events/${eventItem.id}`, { album_price: Number(price) });
      toast.success("Album price updated");
      await load();
    } catch (error) { toast.error(apiError(error)); }
  }

  async function uploadAuthCover(event) {
    event.preventDefault();
    if (!authCoverFile) return toast.error("Choose an image file first");
    setUploadingCover(true);
    try {
      const data = new FormData();
      data.append("file", authCoverFile);
      const response = await api.post("/admin/settings/auth-cover", data);
      setAuthCover(response.data);
      setAuthCoverFile(null);
      toast.success("Sign-in page image updated");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setUploadingCover(false);
    }
  }

  async function resetAuthCover() {
    try {
      const response = await api.delete("/admin/settings/auth-cover");
      setAuthCover(response.data);
      toast.success("Sign-in page image reset to default");
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  async function toggleMessageRead(message) {
    try {
      const response = await api.put(`/admin/messages/${message.id}/read`);
      setMessages((current) => current.map((entry) => (entry.id === message.id ? { ...entry, read_at: response.data.read_at } : entry)));
    } catch (error) {
      toast.error(apiError(error));
    }
  }

  async function applyPhotoPriceToEvent(eventItem, price) {
    try {
      const response = await api.put(`/admin/events/${eventItem.id}/photo-price`, { price: Number(price) });
      toast.success(`Set ${response.data.updated} photo(s) in ${eventItem.title} to $${Number(price).toFixed(2)}`);
      setMediaVersion((version) => version + 1);
      await load();
    } catch (error) { toast.error(apiError(error)); }
  }

  const featuredEvents = events.filter((item) => item.featured);
  const heroEvent = featuredEvents.find((item) => item.hero) || featuredEvents[0] || events[0];

  return (
    <div data-testid="admin-catalog-management-page" className="min-h-screen bg-onyx px-4 pt-32 sm:px-8 lg:px-12">
      <Seo title="Admin Catalog" description="Manage mellowjmedia events, photo uploads, pricing and orders." />
      <div className="mx-auto max-w-7xl pb-24">
        <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">Catalog control</p>
        <h1 className="mt-5 font-display text-5xl font-light text-white sm:text-6xl">mellowjmedia admin.</h1>
        {summary && <div data-testid="admin-metrics-grid" className="mt-10 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-4">
          {[["Events", summary.events], ["Photos", summary.photos], ["Paid orders", summary.orders], ["Revenue", `$${Number(summary.revenue).toFixed(2)}`]].map(([label, value]) => <div key={label} className="bg-carbon p-6"><p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</p><p className="mt-3 font-display text-4xl text-white">{value}</p></div>)}
        </div>}

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          <form data-testid="admin-add-event-form" onSubmit={createEvent} className="border border-white/10 bg-carbon p-6">
            <h2 className="font-display text-3xl text-white">Add event</h2>
            <div className="mt-6 grid gap-4">
              <Input data-testid="admin-event-title-input" required placeholder="Event title" value={eventForm.title} onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })} className="border-white/10 bg-white/[0.03]" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input data-testid="admin-event-date-input" required type="date" value={eventForm.date} onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })} className="border-white/10 bg-white/[0.03]" />
                <Input data-testid="admin-event-location-input" required placeholder="Location" value={eventForm.location} onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })} className="border-white/10 bg-white/[0.03]" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <Select value={eventForm.category} onValueChange={(value) => setEventForm({ ...eventForm, category: value })}>
                  <SelectTrigger data-testid="admin-event-category-select" className="border-white/10 bg-white/[0.03]"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="motorcycles">Motorcycles</SelectItem><SelectItem value="cars">Cars</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
                </Select>
                <Input data-testid="admin-event-price-input" required type="number" min="1" step="0.01" value={eventForm.album_price} onChange={(e) => setEventForm({ ...eventForm, album_price: e.target.value })} className="border-white/10 bg-white/[0.03]" />
              </div>
              <Textarea data-testid="admin-event-description-input" required placeholder="Description" value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} className="min-h-28 border-white/10 bg-white/[0.03]" />
              <label className="flex items-center gap-3 text-sm text-zinc-400"><input data-testid="admin-event-featured-checkbox" type="checkbox" checked={eventForm.featured} onChange={(e) => setEventForm({ ...eventForm, featured: e.target.checked })} /> Feature on home page</label>
              <Button data-testid="admin-add-event-button" disabled={saving} className="rounded-none bg-gold text-onyx hover:bg-titanium">Create event</Button>
            </div>
          </form>

          <form data-testid="admin-upload-photo-form" onSubmit={uploadPhoto} className="border border-white/10 bg-carbon p-6">
            <h2 className="font-display text-3xl text-white">Upload photo</h2>
            <div className="mt-6 grid gap-4">
              <Select value={photoForm.event_id} onValueChange={(value) => setPhotoForm({ ...photoForm, event_id: value })}>
                <SelectTrigger data-testid="admin-photo-event-select" className="border-white/10 bg-white/[0.03]"><SelectValue placeholder="Choose event" /></SelectTrigger>
                <SelectContent>{events.map((eventItem) => <SelectItem key={eventItem.id} value={eventItem.id}>{eventItem.title}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="admin-photo-title-input" required placeholder="Photo title" value={photoForm.title} onChange={(e) => setPhotoForm({ ...photoForm, title: e.target.value })} className="border-white/10 bg-white/[0.03]" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input data-testid="admin-photo-price-input" type="number" min="1" step="0.01" value={photoForm.price} onChange={(e) => setPhotoForm({ ...photoForm, price: e.target.value })} className="border-white/10 bg-white/[0.03]" />
                <Input data-testid="admin-photo-vehicle-input" placeholder="Vehicle / subject" value={photoForm.vehicle} onChange={(e) => setPhotoForm({ ...photoForm, vehicle: e.target.value })} className="border-white/10 bg-white/[0.03]" />
              </div>
              <Input data-testid="admin-photo-rider-input" placeholder="Rider / driver (optional)" value={photoForm.rider} onChange={(e) => setPhotoForm({ ...photoForm, rider: e.target.value })} className="border-white/10 bg-white/[0.03]" />
              <Input data-testid="admin-photo-exif-input" placeholder='EXIF JSON, e.g. {"iso":"800"}' value={photoForm.exif} onChange={(e) => setPhotoForm({ ...photoForm, exif: e.target.value })} className="border-white/10 bg-white/[0.03] font-mono text-xs" />
              <Input data-testid="admin-photo-file-input" required type="file" accept="image/*" onChange={(e) => setPhotoForm({ ...photoForm, file: e.target.files?.[0] || null })} className="border-white/10 bg-white/[0.03]" />
              <label className="flex items-center gap-3 text-sm text-zinc-400"><input data-testid="admin-auto-label-checkbox" type="checkbox" checked={autoLabel} onChange={(e) => setAutoLabel(e.target.checked)} /> AI label on upload (title, vehicle, search tags)</label>
              <Button data-testid="admin-upload-photo-button" disabled={saving} className="rounded-none bg-gold text-onyx hover:bg-titanium">Upload original + make preview</Button>
            </div>
          </form>
        </div>

        <BulkUpload events={events} onChanged={load} />

        <section data-testid="admin-spotlight-section" className="mt-12 border border-white/10 bg-carbon p-6">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div>
              <h2 className="font-display text-3xl text-white">Home page spotlight</h2>
              <p className="mt-2 max-w-xl text-sm leading-6 text-zinc-500">Choose which albums appear on the front page and pick the one that becomes the big hero image; up to two featured albums also show as cards.</p>
            </div>
            <div className="flex gap-3">
              <Select value={spotlightId} onValueChange={setSpotlightId}>
                <SelectTrigger data-testid="spotlight-event-select" className="w-64 border-white/10 bg-white/[0.03]"><SelectValue placeholder="Choose an album" /></SelectTrigger>
                <SelectContent>{events.filter((item) => !item.featured).map((item) => <SelectItem key={item.id} value={item.id}>{item.title}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="spotlight-feature-button" disabled={!spotlightId} onClick={featureFromSpotlight} className="rounded-none bg-gold text-onyx hover:bg-titanium">Feature</Button>
            </div>
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_380px]">
            <div className="grid content-start gap-3">
              {featuredEvents.length === 0 && <p data-testid="spotlight-empty-state" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">No albums featured — the newest event is used as the hero.</p>}
              {featuredEvents.map((eventItem) => (
                <div key={eventItem.id} data-testid={`spotlight-featured-row-${eventItem.id}`} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 p-4">
                  <div className="flex items-center gap-4">
                    {eventItem.cover_url && <img src={eventItem.cover_url} alt="" className="h-12 w-16 object-cover" />}
                    <div>
                      <p className="font-lux text-white">{eventItem.title}</p>
                      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{eventItem.date} · {eventItem.location}</p>
                    </div>
                    {eventItem.id === heroEvent?.id && <span data-testid="spotlight-hero-badge" className="border border-gold/40 bg-gold/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold">Hero</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {eventItem.id !== heroEvent?.id && (
                      <button data-testid={`spotlight-make-hero-${eventItem.id}`} onClick={() => makeHero(eventItem)} className="border border-gold/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx">Make hero</button>
                    )}
                    <button data-testid={`spotlight-remove-${eventItem.id}`} onClick={() => toggleFeatured(eventItem)} className="border border-white/15 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-400 transition-colors hover:border-gold/50 hover:text-gold">Remove</button>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-zinc-500">Home hero preview</p>
              <div data-testid="spotlight-hero-preview" className="relative mt-3 aspect-[16/10] overflow-hidden border border-gold/20 bg-onyx">
                {heroEvent?.cover_url && <img src={heroEvent.cover_url} alt={`${heroEvent.title} home hero preview`} className="absolute inset-0 h-full w-full object-cover opacity-60" />}
                <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,7,8,0.96),rgba(7,7,8,0.5)_48%,rgba(7,7,8,0.18)),linear-gradient(0deg,#070708_0%,transparent_40%)]" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <p className="font-mono text-[8px] uppercase tracking-[0.4em] text-gold">mellowjmedia / event archive</p>
                  <p className="mt-2 font-display text-2xl font-light leading-[1.05] tracking-[-0.03em] text-white">Machines in <span className="italic text-titanium">their most</span> <span className="text-gold">honest light.</span></p>
                  <p data-testid="spotlight-hero-preview-album" className="mt-3 border-t border-gold/20 pt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">Hero album · {heroEvent ? heroEvent.title : "No events yet"}</p>
                </div>
              </div>
              <p className="mt-3 font-mono text-[10px] uppercase leading-5 tracking-[0.18em] text-zinc-600">Updates instantly when you feature albums or set a new cover in the media library</p>
            </div>
          </div>
        </section>

        <section data-testid="admin-auth-cover-section" className="mt-12 border border-white/10 bg-carbon p-6">
          <h2 className="font-display text-3xl text-white">Sign-in page image</h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-zinc-500">This photo sits beside the login and registration form your collectors see. Upload any image to replace it.</p>
          <div className="mt-6 grid gap-6 lg:grid-cols-[380px_1fr] lg:items-start">
            <div data-testid="auth-cover-preview" className="relative aspect-[3/4] max-h-72 overflow-hidden border border-gold/20 bg-onyx">
              {authCover?.url && <img src={authCover.url} alt="Current sign-in page" className="absolute inset-0 h-full w-full object-cover" />}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(7,7,8,0.45),rgba(7,7,8,0.88))]" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <p className="font-mono text-[8px] uppercase tracking-[0.4em] text-gold">Collector access</p>
                <p className="mt-2 font-display text-xl font-light leading-tight text-white">Keep every original within reach.</p>
              </div>
            </div>
            <form data-testid="auth-cover-form" onSubmit={uploadAuthCover} className="grid content-start gap-4">
              <Input data-testid="auth-cover-file-input" type="file" accept="image/*" onChange={(e) => setAuthCoverFile(e.target.files?.[0] || null)} className="border-white/10 bg-white/[0.03]" />
              <Button data-testid="auth-cover-upload-button" disabled={uploadingCover || !authCoverFile} className="w-fit rounded-none bg-gold px-7 text-onyx hover:bg-titanium">{uploadingCover ? "Uploading" : "Set sign-in image"}</Button>
              {authCover?.custom && <Button data-testid="auth-cover-reset-button" type="button" variant="outline" onClick={resetAuthCover} className="w-fit rounded-none border-white/15 px-7 text-zinc-300 hover:border-gold/50 hover:text-gold">Reset to default</Button>}
              <p data-testid="auth-cover-status" className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600">{authCover?.custom ? "Using your custom image" : "Using the default stock image"}</p>
            </form>
          </div>
        </section>

        <section data-testid="admin-pricing-table" className="mt-12 border border-white/10 bg-carbon p-6">
          <h2 className="font-display text-3xl text-white">Album pricing & attendees</h2>
          <div className="mt-6 grid gap-4">
            {events.map((eventItem) => <AdminPriceRow key={eventItem.id} eventItem={eventItem} onSave={updateAlbumPrice} subscriberCount={subscribers.filter((sub) => sub.event_id === eventItem.id).length} onNotify={notifyEvent} onToggleFeatured={toggleFeatured} onEdit={updateEventDetails} onSetPhotoPrices={applyPhotoPriceToEvent} />)}
          </div>
        </section>

        <PromoManager events={events} />

        <MediaLibrary events={events} onChanged={load} refreshKey={mediaVersion} />

        <section data-testid="admin-users-section" className="mt-12 border border-white/10 bg-carbon p-6">
          <h2 className="font-display text-3xl text-white">Registered users</h2>
          <p className="mt-2 max-w-xl text-sm leading-6 text-zinc-500">Every account on the site and the event photo-drop alerts each one subscribed to.</p>
          <div data-testid="admin-users-table" className="mt-6 overflow-x-auto">
            {users.length === 0 && <p data-testid="admin-users-empty" className="border border-white/10 p-8 text-sm text-zinc-500">No registered users yet.</p>}
            {users.length > 0 && (
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Joined</th><th className="p-3">Subscribed to</th></tr></thead>
                <tbody>
                  {users.map((account) => (
                    <tr key={account.id} data-testid={`admin-user-row-${account.id}`} className="border-t border-white/10">
                      <td className="p-3 text-white">{account.name || "—"}</td>
                      <td className="p-3 text-zinc-300">{account.email}</td>
                      <td className="p-3"><span className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.16em] ${account.role === "admin" ? "border-gold/40 text-gold" : "border-white/15 text-zinc-400"}`}>{account.role}</span></td>
                      <td className="p-3 font-mono text-xs text-zinc-500">{account.created_at ? new Date(account.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</td>
                      <td className="p-3" data-testid={`admin-user-subscriptions-${account.id}`}>{account.subscriptions.length > 0 ? <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-gold">{account.subscriptions.join(" · ")}</span> : <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-600">None</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section data-testid="admin-messages-section" className="mt-12 border border-white/10 bg-carbon p-6">
          <div className="flex flex-wrap items-center gap-4">
            <h2 className="font-display text-3xl text-white">Messages</h2>
            {messages.filter((entry) => entry.entry_type === "contact" && !entry.read_at).length > 0 && (
              <span data-testid="messages-unread-badge" className="border border-gold/40 bg-gold/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">{messages.filter((entry) => entry.entry_type === "contact" && !entry.read_at).length} new</span>
            )}
          </div>
          <p className="mt-2 max-w-xl text-sm leading-6 text-zinc-500">Every customer touchpoint, newest first — contact enquiries (also emailed to studio@mellowjmedia.com) and completed orders.</p>
          <div className="mt-6 grid gap-4">
            {messages.length === 0 && <p data-testid="admin-messages-empty" className="border border-white/10 p-8 text-sm text-zinc-500">No activity yet.</p>}
            {messages.map((entry) => entry.entry_type === "order" ? (
              <div key={`order-${entry.id}`} data-testid={`admin-order-entry-${entry.id}`} className="border border-gold/20 bg-gold/[0.03] p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">Order · {entry.customer_email || "Guest"}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{entry.created_at ? new Date(entry.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="border border-gold/40 bg-gold/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-gold">Paid</span>
                    {entry.email_sent_at && <span className="border border-white/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400">Download email sent</span>}
                  </div>
                </div>
                <p data-testid={`admin-order-total-${entry.id}`} className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white">${Number(entry.total || 0).toFixed(2)} · {(entry.items || []).length} item(s)</p>
                <p className="mt-2 text-sm leading-6 text-zinc-400">{(entry.items || []).map((item) => item.title).join(" · ")}</p>
              </div>
            ) : (
              <div key={entry.id} data-testid={`admin-message-${entry.id}`} className={`border p-5 ${entry.read_at ? "border-white/10 opacity-70" : "border-gold/30"}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-lux text-white">{entry.name} <span className="text-zinc-500">· {entry.email}</span></p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{entry.created_at ? new Date(entry.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!entry.read_at && <span data-testid={`admin-message-new-${entry.id}`} className="border border-gold/40 bg-gold/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-gold">New</span>}
                    {entry.status === "emailed" && <span className="border border-white/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400">Studio notified</span>}
                    {entry.autoreplied_at && <span className="border border-white/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400">Autoreply sent</span>}
                    <button data-testid={`admin-message-read-${entry.id}`} onClick={() => toggleMessageRead(entry)} className="border border-white/15 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.18em] text-zinc-400 transition-colors hover:border-gold/50 hover:text-gold">{entry.read_at ? "Mark unread" : "Mark read"}</button>
                  </div>
                </div>
                <p data-testid={`admin-message-subject-${entry.id}`} className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-gold">{entry.subject}</p>
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-zinc-300">{entry.message}</p>
                <a data-testid={`admin-message-reply-${entry.id}`} href={`mailto:${entry.email}?subject=Re: ${encodeURIComponent(entry.subject)}`} className="mt-4 inline-flex items-center gap-2 border border-gold/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx">Reply by email</a>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 border border-white/10 bg-carbon p-6">
          <h2 className="font-display text-3xl text-white">Recent orders</h2>
          <div data-testid="admin-orders-table" className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500"><tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Total</th><th className="p-3">Status</th></tr></thead>
              <tbody>{orders.map((order) => <tr key={order.id} data-testid={`admin-order-row-${order.id}`} className="border-t border-white/10"><td className="p-3 font-mono text-xs">{order.id.slice(0, 8)}</td><td className="p-3">{order.customer_email || "Guest"}</td><td className="p-3">{order.items?.length || 0}</td><td className="p-3">${Number(order.total).toFixed(2)}</td><td className="p-3 text-gold">{order.payment_status}</td></tr>)}</tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function AdminPriceRow({ eventItem, onSave, subscriberCount, onNotify, onToggleFeatured, onEdit, onSetPhotoPrices }) {
  const [price, setPrice] = useState(eventItem.album_price);
  const [photoPrice, setPhotoPrice] = useState("");
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: eventItem.title, date: eventItem.date, location: eventItem.location, category: eventItem.category, description: eventItem.description });

  async function submitEdit(event) {
    event.preventDefault();
    const saved = await onEdit(eventItem, editForm);
    if (saved) setEditing(false);
  }

  function applyPhotoPrice() {
    const value = Number(photoPrice);
    if (!value || value <= 0) return toast.error("Enter a valid photo price");
    if (!window.confirm(`Set all ${eventItem.photo_count} photo(s) in "${eventItem.title}" to $${value.toFixed(2)} each?`)) return;
    onSetPhotoPrices(eventItem, value);
    setPhotoPrice("");
  }

  return (
    <div data-testid={`admin-price-row-${eventItem.id}`} className="border border-white/10 p-4">
      <div className="grid gap-3 lg:grid-cols-[1fr_110px_90px_180px_110px_80px_110px_190px] lg:items-center">
        <div><p className="font-lux text-white">{eventItem.title}</p><p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{eventItem.photo_count} photos</p></div>
        <Input data-testid={`admin-album-price-input-${eventItem.id}`} type="number" min="1" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="border-white/10 bg-white/[0.03]" />
        <Button data-testid={`admin-save-price-button-${eventItem.id}`} onClick={() => onSave(eventItem, price)} variant="outline" className="rounded-none border-gold/40 text-gold hover:bg-gold hover:text-onyx">Save</Button>
        <div className="flex items-center gap-2">
          <Input data-testid={`admin-photo-price-all-input-${eventItem.id}`} type="number" min="1" step="0.01" placeholder="All frames $" value={photoPrice} onChange={(e) => setPhotoPrice(e.target.value)} className="h-9 w-28 border-white/10 bg-white/[0.03] text-xs" />
          <button data-testid={`admin-photo-price-all-apply-${eventItem.id}`} onClick={applyPhotoPrice} className="whitespace-nowrap border border-gold/40 px-2 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-gold transition-colors hover:bg-gold hover:text-onyx">Set all</button>
        </div>
        <button data-testid={`featured-toggle-${eventItem.id}`} onClick={() => onToggleFeatured(eventItem)} className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${eventItem.featured ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-400 hover:border-gold/50 hover:text-gold"}`}>
          {eventItem.featured ? "★ Featured" : "Feature"}
        </button>
        <button data-testid={`edit-event-${eventItem.id}`} onClick={() => setEditing((current) => !current)} className={`border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${editing ? "border-gold bg-gold/15 text-gold" : "border-white/15 text-zinc-400 hover:border-gold/50 hover:text-gold"}`}>
          {editing ? "Close" : "Edit"}
        </button>
        <Link data-testid={`admin-print-handout-${eventItem.id}`} to={`/admin/handout/${eventItem.slug}`} className="inline-flex items-center justify-center border border-white/15 px-4 py-2 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold">Print card</Link>
        <div className="flex items-center gap-2">
          <span data-testid={`subscriber-count-${eventItem.id}`} className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">{subscriberCount} subscribed</span>
          <button data-testid={`notify-subscribers-${eventItem.id}`} onClick={() => onNotify(eventItem, subscriberCount)} disabled={!subscriberCount} className="border border-gold/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.16em] text-gold transition-colors hover:bg-gold hover:text-onyx disabled:cursor-not-allowed disabled:opacity-40">
            Notify
          </button>
        </div>
      </div>
      {editing && (
        <form data-testid={`admin-edit-event-form-${eventItem.id}`} onSubmit={submitEdit} className="mt-4 grid gap-4 border-t border-white/10 pt-4">
          <Input data-testid={`edit-event-title-${eventItem.id}`} required placeholder="Event title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="border-white/10 bg-white/[0.03]" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input data-testid={`edit-event-date-${eventItem.id}`} required type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="border-white/10 bg-white/[0.03]" />
            <Input data-testid={`edit-event-location-${eventItem.id}`} required placeholder="Location" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} className="border-white/10 bg-white/[0.03]" />
          </div>
          <Select value={editForm.category} onValueChange={(value) => setEditForm({ ...editForm, category: value })}>
            <SelectTrigger data-testid={`edit-event-category-${eventItem.id}`} className="border-white/10 bg-white/[0.03]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="motorcycles">Motorcycles</SelectItem><SelectItem value="cars">Cars</SelectItem><SelectItem value="both">Both</SelectItem></SelectContent>
          </Select>
          <Textarea data-testid={`edit-event-description-${eventItem.id}`} required placeholder="Description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="min-h-24 border-white/10 bg-white/[0.03]" />
          <div className="flex gap-3">
            <Button data-testid={`edit-event-save-${eventItem.id}`} type="submit" className="rounded-none bg-gold text-onyx hover:bg-titanium">Save changes</Button>
            <Button data-testid={`edit-event-cancel-${eventItem.id}`} type="button" variant="outline" onClick={() => setEditing(false)} className="rounded-none border-white/15 text-zinc-300 hover:border-gold/50 hover:text-gold">Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
